const RELOAD_ATTEMPT_KEY = 'vatix.staleChunkReloadAt';
// A stale chunk fails again within milliseconds of the fresh load if the asset is
// genuinely gone, so a cooldown breaks the reload loop while still letting a tab
// recover from a later deploy.
const RELOAD_COOLDOWN_MS = 10_000;

// A stale lazy chunk raises a different message per browser, and the URL in it carries
// the content hash — so grouping by message alone spawns a fresh Sentry issue per deploy.
const CHUNK_ERROR_PATTERNS = [
  'failed to fetch dynamically imported module',
  'error loading dynamically imported module',
  'importing a module script failed',
  'failed to load module script',
  'unable to preload css for',
  'chunkloaderror',
];

const errorMessage = (error: unknown): string => {
  if (typeof error === 'string') return error;
  if (error instanceof Error) return `${error.name}: ${error.message}`;
  if (error && typeof error === 'object' && 'message' in error) {
    return String((error as { message: unknown }).message);
  }
  return '';
};

export const isStaleChunkError = (error: unknown): boolean => {
  const message = errorMessage(error).toLowerCase();
  return message !== '' && CHUNK_ERROR_PATTERNS.some((pattern) => message.includes(pattern));
};

// Mirrors the stored timestamp so the cooldown still holds where sessionStorage
// throws (private mode, blocked site data) — without it every failure reloads.
let lastAttemptInMemory = 0;

const readLastAttempt = (): number => {
  try {
    return Math.max(Number(window.sessionStorage.getItem(RELOAD_ATTEMPT_KEY)) || 0, lastAttemptInMemory);
  } catch {
    return lastAttemptInMemory;
  }
};

const writeLastAttempt = (at: number): void => {
  lastAttemptInMemory = at;
  try {
    window.sessionStorage.setItem(RELOAD_ATTEMPT_KEY, String(at));
  } catch {
    /* sessionStorage unavailable — the in-memory copy carries the cooldown */
  }
};

export const reloadOnceForStaleChunk = (): boolean => {
  const now = Date.now();
  if (now - readLastAttempt() < RELOAD_COOLDOWN_MS) return false;

  writeLastAttempt(now);
  window.location.reload();
  return true;
};

// In-memory only: a failure on the reloaded page means recovery failed, so Sentry must see it.
export const isReloadingForStaleChunk = (): boolean =>
  lastAttemptInMemory !== 0 && Date.now() - lastAttemptInMemory < RELOAD_COOLDOWN_MS;

export const registerStaleChunkRecovery = (): void => {
  // Vite's preload helper wraps every built dynamic import and dispatches this
  // for any of them, which is why the React.lazy call sites need no wrapper.
  window.addEventListener('vite:preloadError', () => {
    reloadOnceForStaleChunk();
  });

  window.addEventListener('unhandledrejection', (event) => {
    if (isStaleChunkError(event.reason)) {
      reloadOnceForStaleChunk();
    }
  });
};
